import {
  ChatSession,
  useChatbotSession,
} from 'features/chatbot/contexts/ChatbotSessionContext'
import { Check, X } from 'lucide-react'
import React, { useEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'

const HOVER_BG = 'rgba(255, 255, 255, 0.06)'

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 0.125rem;
  color: ${({ theme }) => theme.forecast.tertiaryText};
  cursor: pointer;
  visibility: hidden;
  flex-shrink: 0;

  &:hover {
    color: ${({ theme }) => theme.forecast.secondaryText};
  }

  svg {
    width: 0.75rem;
    height: 0.75rem;
  }
`

const Tab = styled.div<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  position: relative;
  gap: 0.25rem;
  padding: 0.25rem 0.375rem;
  border-radius: 0.25rem;
  user-select: none;
  white-space: nowrap;
  font-size: 0.75rem;
  max-width: 10rem;
  cursor: pointer;
  background: transparent;
  transition:
    background 0.15s ease,
    color 0.15s ease;

  &:hover ${CloseButton} {
    visibility: visible;
  }

  ${({ $isActive }) =>
    $isActive
      ? css`
          font-weight: 600;
          color: ${({ theme }) => theme.forecast.defaultText};
        `
      : css`
          font-weight: 400;
          color: ${({ theme }) => theme.forecast.tertiaryText};
          &:hover {
            background: ${HOVER_BG};
            color: ${({ theme }) => theme.forecast.secondaryText};
          }
        `}
`

const TabLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`

const RenameInput = styled.input`
  width: 100%;
  padding: 0.125rem 0.25rem;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 0.25rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.75rem;
  outline: none;

  &:focus {
    border-color: rgba(255, 255, 255, 0.4);
  }
`

const RenameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.125rem;
  flex: 1;
  min-width: 0;
`

const ConfirmButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 0.125rem;
  color: ${({ theme }) => theme.forecast.tertiaryText};
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    color: ${({ theme }) => theme.forecast.secondaryText};
  }

  svg {
    width: 0.75rem;
    height: 0.75rem;
  }
`

const ContextMenu = styled.div`
  position: fixed;
  z-index: 1100;
  background: #1e1e1e;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 0.375rem;
  padding: 0.25rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  min-width: 7rem;
`

const ContextMenuItem = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.375rem 0.5rem;
  background: transparent;
  border: none;
  border-radius: 0.25rem;
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.8125rem;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`

interface ContextMenuState {
  sessionId: string
  x: number
  y: number
}

function getSessionDisplayName(session: ChatSession): string {
  return session.name ?? 'New Chat'
}

export const SessionTabs: React.FC = () => {
  const {
    openSessions,
    activeSession,
    switchSession,
    closeSession,
    renameSession,
  } = useChatbotSession()

  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null)
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const renameInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (renamingId && renameInputRef.current) {
      renameInputRef.current.focus()
      renameInputRef.current.select()
    }
  }, [renamingId])

  const canClose = openSessions.length > 1

  const handleTabClick = (id: string) => {
    if (renamingId === id) return
    switchSession(id)
  }

  const handleContextMenu = (e: React.MouseEvent, id: string) => {
    e.preventDefault()
    setContextMenu({ sessionId: id, x: e.clientX, y: e.clientY })
  }

  const handleStartRename = () => {
    if (!contextMenu) return
    const session = openSessions.find((s) => s.id === contextMenu.sessionId)
    if (session) {
      setRenameValue(getSessionDisplayName(session))
      setRenamingId(contextMenu.sessionId)
    }
    setContextMenu(null)
  }

  const handleConfirmRename = () => {
    if (renamingId) {
      const trimmed = renameValue.trim()
      if (trimmed) {
        renameSession(renamingId, trimmed)
      }
      setRenamingId(null)
    }
  }

  const handleCancelRename = () => {
    setRenamingId(null)
  }

  const handleRenameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleConfirmRename()
    } else if (e.key === 'Escape') {
      handleCancelRename()
    }
  }

  const handleClose = (e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    closeSession(id)
  }

  return (
    <>
      {openSessions.map((session) => (
        <Tab
          key={session.id}
          $isActive={activeSession.id === session.id}
          onClick={() => handleTabClick(session.id)}
          onContextMenu={(e) => handleContextMenu(e, session.id)}>
          {renamingId === session.id ? (
            <RenameContainer>
              <RenameInput
                ref={renameInputRef}
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={handleRenameKeyDown}
                onBlur={handleConfirmRename}
                onClick={(e) => e.stopPropagation()}
              />
              <ConfirmButton
                onClick={(e) => {
                  e.stopPropagation()
                  handleConfirmRename()
                }}
                aria-label='Confirm rename'>
                <Check />
              </ConfirmButton>
            </RenameContainer>
          ) : (
            <TabLabel>{getSessionDisplayName(session)}</TabLabel>
          )}
          {renamingId !== session.id && canClose && (
            <CloseButton
              onClick={(e) => handleClose(e, session.id)}
              aria-label='Close session'>
              <X />
            </CloseButton>
          )}
        </Tab>
      ))}

      {contextMenu && (
        <SessionTabsContextMenuOverlay
          contextMenu={contextMenu}
          onStartRename={handleStartRename}
          onClose={() => setContextMenu(null)}
        />
      )}
    </>
  )
}

const SessionTabsContextMenuOverlay: React.FC<{
  contextMenu: ContextMenuState
  onStartRename: () => void
  onClose: () => void
}> = ({ contextMenu, onStartRename, onClose }) => {
  useEffect(() => {
    const handleClick = () => onClose()
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [onClose])

  return (
    <ContextMenu
      style={{ top: contextMenu.y, left: contextMenu.x }}
      onClick={(e) => e.stopPropagation()}>
      <ContextMenuItem onClick={onStartRename}>Rename</ContextMenuItem>
    </ContextMenu>
  )
}
